import clsx from "clsx";
import React, { FunctionComponent, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "wouter";

import { useLanguage } from "../../../i18n/useLanguage";
import { PackagePartItem } from "../../../queries/useSearchPartPackagesByVehicle";
import { useRelatedCartItems } from "../../../stores/hooks/useRelatedCartItems";
import { Button } from "../../../ui/Button/Button";
import { KeyValue } from "../../../ui/KeyValue/KeyValue";
import { Placeholder } from "../../../ui/Placeholder";
import { InfoIcon } from "../../../ui/icons/InfoIcon";
import { getPartName } from "../../../util/getPartName";
import { CartButton } from "../../Cart/CartButton/CartButton";
import { PriceSelect } from "../../PriceSelect/PriceSelect";
import { usePriceSelectState } from "../../PriceSelect/usePriceSelectState";
import { getProductModalHash } from "../../ProductDetails/ProductModal";
import { useRebateLinks } from "../../Tires/Results/Tile/Rebates";
import { PackageImages } from "../PackageImages/PackageImages";
import { PackageItemList } from "./PackageItemList";

type Props = {
  onSelect: ((packageId: string) => void) | null;
  part: PackagePartItem | null;
  selected: boolean;
  vehicleId?: string;
};

export const PackageTileRow: FunctionComponent<Props> = ({
  onSelect,
  part,
  selected,
  vehicleId,
}) => {
  const [language] = useLanguage();
  const { t } = useTranslation();
  const [orderItem] = useRelatedCartItems(part, false);
  const [priceOption, setPiceOption] = usePriceSelectState([]);

  const packageId = part?.partDetails.primaryPart.itemId;
  const detailsLink = getProductModalHash(packageId);

  const select = useCallback((): void => {
    packageId && onSelect && onSelect(packageId);
  }, [packageId, onSelect]);

  const tirePart = part?.partTire;

  const promotions = useRebateLinks(
    language,
    part?.partPricing.primaryPart.link,
    priceOption[0]?.pricingOption.quantity
  );

  return (
    <figure
      className={clsx(
        "tw-package-tile",
        selected && "tw-package-tile--selected"
      )}
    >
      <PackageImages part={part} select={select} />
      <div className="col-span-2 md:col-span-3 mt-2 md:mt-0" onClick={select}>
        {tirePart && (
          <img
            alt="Tire manufacturer logo"
            className="h-6 mb-2 mx-auto block md:mx-0"
            src={tirePart.partDetails.primaryPart.tireManufacturerImage}
          />
        )}
        <h2 className="font-semibold md:text-lg md:leading-5 md:mb-2">
          <Placeholder className="w-40" loading={!part}>
            {getPartName(part, language)}
          </Placeholder>
        </h2>
        <KeyValue className="text-sm col-span-3">
          <>{t("searchResults.partNumber")}:</>
          <Placeholder className="w-24" loading={!part}>
            {part?.partDetails.primaryPart.packagePartNumber}
          </Placeholder>
        </KeyValue>
        <PackageItemList
          className="text-sm"
          primaryPart={part?.partDetails.primaryPart}
        />
        {Boolean(promotions.length) && (
          <div className="flex flex-wrap mt-1 gap-0.5">
            {promotions.map(({ linkText, linkURL, promotionText }, key) => {
              const href = linkURL?.[language] || linkURL?.en;
              const linkName = linkText?.[language] || linkText?.en;
              return (
                <div
                  className="text-xs bg-rebate rounded-tw-full px-2"
                  key={key}
                >
                  {linkText && href ? (
                    <a
                      className="flex justify-between items-center underline"
                      href={href}
                      key={key}
                      rel="noreferrer"
                      target="_blank"
                    >
                      {linkName}
                    </a>
                  ) : (
                    <span
                      className="flex justify-between items-center"
                      key={key}
                    >
                      {promotionText[language] || promotionText.en}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
      <div className="col-span-2">
        <div className="grid grid-cols-2 gap-1 md:gap-2">
          <PriceSelect
            disabled={!!orderItem}
            isStaggered={false}
            itemPriceClassName="text-sm"
            onChange={setPiceOption}
            onReady={setPiceOption}
            orderItem={orderItem}
            part={part}
            promotionClassName="text-sm"
            quantityClassName="text-sm"
            totalClassName="order-2"
          />
        </div>
      </div>
      <div className="grid col-span-2 gap-1 mt-2 md:mt-0">
        <CartButton
          className="tw-package-tile__cart-button bg-primary"
          createPackageClassName={null}
          isStaggered={false}
          part={part}
          pricing={priceOption}
          vehicleId={Number(vehicleId)}
          vehicleInfo={null}
        />
        <Link to={detailsLink}>
          <Button aria-label="viewDetails" disabled={!part} element="a">
            <InfoIcon />
            {t("searchResults.viewDetails")}
          </Button>
        </Link>
      </div>
    </figure>
  );
};
